import api from './api';

// Função para formatar moeda brasileira
function formatCurrency(value) {
    if (value === null || value === undefined || isNaN(value)) return 'R$ 0,00';
    
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(value);
}

// Função para formatar data para exibição
function formatDate(dateString) {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        
        return date.toLocaleDateString('pt-BR');
    } catch (e) {
        return '-';
    }
}

// Função para formatar período (mês/ano)
function formatPeriod(year, month) {
    if (!year || !month) return '-';
    
    const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    
    return `${monthNames[month - 1]} ${year}`;
}

const financialService = {
    // Obter estatísticas financeiras (NOVO ENDPOINT)
    async getFinancialStats(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.personId) params.append('personId', filters.personId);
            if (filters.startDate) params.append('start', filters.startDate);
            if (filters.endDate) params.append('end', filters.endDate);

            const response = await api.get(`/appointment/financial-stats?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar estatísticas financeiras:', error);
            throw new Error('Não foi possível buscar as estatísticas financeiras.');
        }
    },

    // Obter projeção de pagamentos
    async getPaymentProjection(filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.personId) params.append('personId', filters.personId);
            if (filters.startDate) params.append('start', filters.startDate);
            if (filters.endDate) params.append('end', filters.endDate);

            const response = await api.get(`/appointment/payment-projection?${params.toString()}`);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar projeção de pagamentos:', error);
            throw new Error('Não foi possível buscar a projeção de pagamentos.');
        }
    },

    // Atualizar informações de pagamento
    async updatePayment(appointmentId, paymentData) {
        try {
            const response = await api.put(`/appointment/${appointmentId}/payment`, paymentData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar pagamento:', error);
            throw new Error('Não foi possível atualizar o pagamento.');
        }
    },

    // Obter relatório financeiro geral
    async getFinancialReport(params = {}) {
        try {
            let url = '/appointment/';
            const queryParams = new URLSearchParams();
            
            // Filtros de data
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            if (params.status) queryParams.append('status', params.status);
            
            // Buscar apenas consultas concluídas para relatório financeiro
            queryParams.append('status', 'COMPLETED');
            
            if (queryParams.toString()) {
                url += '?' + queryParams.toString();
            }
            
            const response = await api.get(url);
            const appointments = Array.isArray(response.data) ? response.data : response.data.data || [];
            
            // Calcular métricas financeiras
            const totalReceita = appointments
                .filter(app => app.isPaid)
                .reduce((sum, app) => sum + (app.consultationValue || 0), 0);
            
            const totalPendente = appointments
                .filter(app => !app.isPaid)
                .reduce((sum, app) => sum + (app.consultationValue || 0), 0);
            
            const totalConsultas = appointments.length;
            const consultasPagas = appointments.filter(app => app.isPaid).length;
            const consultasPendentes = appointments.filter(app => !app.isPaid).length;
            
            return {
                totalReceita,
                totalPendente,
                totalConsultas,
                consultasPagas,
                consultasPendentes,
                appointments
            };
        } catch (error) {
            console.error('Erro ao buscar relatório financeiro:', error);
            throw new Error('Não foi possível carregar o relatório financeiro.');
        }
    },

    // Obter receita por período (mensal)
    async getMonthlyRevenue(year) {
        try {
            const startDate = `${year}-01-01`;
            const endDate = `${year}-12-31`;
            
            const response = await api.get(`/appointment/?startDate=${startDate}&endDate=${endDate}&status=COMPLETED`);
            const appointments = Array.isArray(response.data) ? response.data : response.data.data || [];
            
            // Agrupar por mês
            const monthlyData = {};
            
            appointments.forEach(appointment => {
                if (!appointment.isPaid) return;
                
                const date = new Date(appointment.dateTime);
                const month = date.getMonth() + 1; // 1-12
                const value = appointment.consultationValue || 0;
                
                if (!monthlyData[month]) {
                    monthlyData[month] = {
                        month,
                        year,
                        revenue: 0,
                        consultations: 0
                    };
                }
                
                monthlyData[month].revenue += value;
                monthlyData[month].consultations += 1;
            });
            
            // Converter para array e preencher meses vazios
            const result = [];
            for (let month = 1; month <= 12; month++) {
                result.push(monthlyData[month] || {
                    month,
                    year,
                    revenue: 0,
                    consultations: 0
                });
            }
            
            return result;
        } catch (error) {
            console.error('Erro ao buscar receita mensal:', error);
            throw new Error('Não foi possível carregar a receita mensal.');
        }
    },

    // Obter consultas pendentes de pagamento
    async getPendingPayments(params = {}) {
        try {
            let url = '/appointment/';
            const queryParams = new URLSearchParams();
            
            // Filtrar apenas consultas concluídas e não pagas
            queryParams.append('status', 'COMPLETED');
            queryParams.append('isPaid', 'false');
            
            if (params.startDate) queryParams.append('startDate', params.startDate);
            if (params.endDate) queryParams.append('endDate', params.endDate);
            if (params.personId) queryParams.append('personId', params.personId);
            
            if (queryParams.toString()) {
                url += '?' + queryParams.toString();
            }
            
            const response = await api.get(url);
            return Array.isArray(response.data) ? response.data : response.data.data || [];
        } catch (error) {
            console.error('Erro ao buscar pagamentos pendentes:', error);
            throw new Error('Não foi possível carregar os pagamentos pendentes.');
        }
    },

    // Marcar consulta como paga
    async markAsPaid(appointmentId, paymentData) {
        try {
            const data = {
                isPaid: true,
                paymentMethod: paymentData.paymentMethod || '',
                paymentDate: paymentData.paymentDate || new Date().toISOString(),
                notes: paymentData.notes || ''
            };
            
            const response = await api.put(`/appointment/${appointmentId}/payment`, data);
            return response.data;
        } catch (error) {
            console.error('Erro ao marcar como pago:', error);
            throw new Error('Não foi possível registrar o pagamento.');
        }
    },

    // Obter estatísticas do dashboard
    async getDashboardStats(params = {}) {
        try {
            // Definir período padrão (mês atual)
            const now = new Date();
            const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            
            const startDate = params.startDate || startOfMonth.toISOString().split('T')[0];
            const endDate = params.endDate || endOfMonth.toISOString().split('T')[0];
            
            // Buscar agendamentos do período
            const appointmentsResponse = await api.get(`/appointment/?startDate=${startDate}&endDate=${endDate}`);
            const appointments = Array.isArray(appointmentsResponse.data) ? 
                appointmentsResponse.data : appointmentsResponse.data.data || [];
            
            // Buscar total de pacientes
            const patientsResponse = await api.get('/person/');
            const patients = Array.isArray(patientsResponse.data) ? 
                patientsResponse.data : patientsResponse.data.data || [];
            
            // Calcular estatísticas
            const totalPacientes = patients.length;
            const consultasAgendadas = appointments.filter(a => a.status === 'SCHEDULED').length;
            const consultasConcluidas = appointments.filter(a => a.status === 'COMPLETED').length;
            const consultasCanceladas = appointments.filter(a => a.status === 'CANCELLED').length;
            
            const receitaTotal = appointments
                .filter(a => a.status === 'COMPLETED' && a.isPaid)
                .reduce((sum, a) => sum + (a.consultationValue || 0), 0);
            
            const receitaPendente = appointments
                .filter(a => a.status === 'COMPLETED' && !a.isPaid)
                .reduce((sum, a) => sum + (a.consultationValue || 0), 0);
            
            return {
                period: { startDate, endDate },
                totalPacientes,
                consultasAgendadas,
                consultasConcluidas,
                consultasCanceladas,
                receitaTotal,
                receitaPendente,
                totalConsultas: appointments.length
            };
        } catch (error) {
            console.error('Erro ao buscar estatísticas do dashboard:', error);
            throw new Error('Não foi possível carregar as estatísticas.');
        }
    },

    // Exportar relatório financeiro
    async exportFinancialReport(params = {}) {
        try {
            const report = await this.getFinancialReport(params);
            
            // Gerar dados para exportação
            const exportData = report.appointments.map(appointment => ({
                'Data/Hora': formatDate(appointment.dateTime),
                'Paciente': appointment.patientName || 'N/A',
                'Status': appointment.status,
                'Valor': formatCurrency(appointment.consultationValue),
                'Pago': appointment.isPaid ? 'Sim' : 'Não',
                'Método de Pagamento': appointment.paymentMethod || '-',
                'Data do Pagamento': appointment.paymentDate ? formatDate(appointment.paymentDate) : '-'
            }));
            
            return exportData;
        } catch (error) {
            console.error('Erro ao exportar relatório:', error);
            throw new Error('Não foi possível exportar o relatório.');
        }
    },

    // Formatadores
    formatters: {
        formatCurrency,
        formatDate,
        formatPeriod,
        
        // Formatar percentual
        formatPercentage(value, total) {
            if (total === 0) return '0%';
            return `${((value / total) * 100).toFixed(1)}%`;
        }
    },

    // Constantes
    constants: {
        paymentMethods: {
            CASH: 'Dinheiro',
            CREDIT_CARD: 'Cartão de Crédito',
            DEBIT_CARD: 'Cartão de Débito',
            PIX: 'PIX',
            BANK_TRANSFER: 'Transferência Bancária',
            CHECK: 'Cheque'
        }
    }
};

export default financialService;